import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { FormikValues } from "formik";
import { ReactElement } from "react";
import { isValid } from "zod";

import {
  UserSubaccountRole,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import {
  FormikDialog,
  FormikErrorBanner,
  FormikSelect,
  FormikSubmitButton,
  FormikTextField,
  submitErrorField,
} from "~frontend/components/forms";
import { SubaccountUser } from "~frontend/components/users/types";

function userRole(user: SubaccountUser): UserSubaccountRole | UserTenantRole {
  if (user.tenantRole === UserTenantRole.None) {
    // SubaccountUsers with no tenant role should only have one subaccount membership
    // association - the one with the current subaccount (others are filtered out at
    // the GQL layer).
    if (user.subaccountMemberships.length != 1) {
      throw new Error("User is missing subaccount membership");
    }
    return user.subaccountMemberships[0].role;
  }

  return user.tenantRole;
}

export function EditUserDialog({
  open,
  user,
  onClose,
  roleOptions,
  onSubmit,
}: {
  open: boolean;
  user: SubaccountUser | null;
  onClose: () => void;
  roleOptions: { value: string; label: string }[];
  onSubmit: (values: FormikValues) => Promise<void>;
}): ReactElement {
  return (
    <FormikDialog
      maxWidth="sm"
      fullWidth
      open={open}
      initialValues={{
        [submitErrorField]: null,
        ...(user
          ? {
              fullName: user.fullName,
              email: user.email,
              role: userRole(user),
            }
          : {
              fullName: "",
              email: "",
              role: "",
            }),
      }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <>
          <DialogTitle>Edit user</DialogTitle>
          <DialogContent>
            <>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Full Name"
                name="fullName"
                disabled={true}
              />
              <FormikTextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                disabled={true}
              />
              <FormikSelect
                fullWidth
                name="role"
                label="Role"
                disabled={roleOptions.length === 1 || isSubmitting}
              >
                {roleOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </FormikSelect>
            </>
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} onClick={onClose} color="secondary">
              Cancel
            </Button>

            <FormikSubmitButton
              disabled={!isValid || isSubmitting}
              color="primary"
              variant="contained"
              submitText="Update"
              loadingText="Updating..."
              data-testid="edit-owned-phone-number-display-name-button"
            />
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
}
